import type { ReactNode } from 'react'
import { useTabManagerContext } from '../../app-layout/lib/TabManagerContext.tsx'
import { formatKeyToLabel } from '../../utils/utilities.ts'
import { FIELD_KEY_SEPARATOR, TABLE_PRIMARY_KEY } from '../../../state_manager/globals.ts'
import { useDrawer } from '../../contexts/DrawerContext.jsx'

import SearchPopover from '../SearchPopover.jsx'
import AddButton from './parts/AddButton.tsx'
import ExportToolbarButton from './parts/ExportToolbarButton.tsx'
import RefreshButton from './parts/RefreshButton.tsx'
import { HelpButton } from '../HelpDrawer/HelpButton.tsx'

export { RIBBON_CTA_CLASS } from './parts/ribbonCta.ts'

interface FilterOption {
  value: string | number
  label: string
}

interface FilterItem {
  key: string
  /** The grids' FilterValue shape: text/multiselect values, or a range's min/max. */
  value: string | string[] | { min?: number | string; max?: number | string }
  index?: number | string
}

/**
 * Search is on by default. `recordTitleField` names the record key whose value titles the tab opened from a
 * search result, so it is only required while search is enabled; a toolbar with `searchEnabled={false}` may omit it.
 */
type ViewToolbarSearchProps =
  | { searchEnabled?: true; recordTitleField: string }
  | { searchEnabled: false; recordTitleField?: string }

interface ViewToolbarBaseProps {
  // Core
  moduleKey: string
  subModuleKey?: string
  // Search
  searchPlaceholder?: string
  /**
   * If true, clicking a search result opens the record's full form (same as the
   * expand action) instead of the default drawer. Use for modules that don't
   * implement a drawer pattern (e.g. funds).
   */
  searchClickOpensFullForm?: boolean
  /** Override the search service identifier (defaults to moduleKey). */
  searchService?: string
  /**
   * Extra context for searches that need more than just a search term —
   * e.g. fund-scoped company search needs `{ fundId }`.
   */
  searchExtraContext?: Record<string, unknown>
  /**
   * Visual variant. `'primary'` (default) uses the bright blue gradient; use
   * `'secondary'` for nested toolbars (e.g. inside a Dashboard tab) where the
   * primary blue would clash with the lighter grey backdrop.
   */
  variant?: 'primary' | 'secondary'
  // Add
  addButtonEnabled?: boolean
  addButtonLabel?: string
  // Refresh
  refreshButtonEnabled?: boolean
  onRefreshFilterOptions?: () => void | Promise<void>
  onRefreshData?: (activeFilters: FilterItem[] | Record<string, any>) => void | Promise<void>
  // Filters
  activeFilters?: FilterItem[] | Record<string, any>
  currentFilterOptions?: Record<string, FilterOption[]>
  /** Declared for the grids that pass their setter; the toolbar itself never invokes it. */
  onFilterChange?: (filters: FilterItem[]) => void
  /** Hide filter tags until filter options are loaded (prevents UUID flash for default filters). */
  filterOptionsLoading?: boolean
  /** Default filters for this view (toolbar Reset restores these). */
  defaultFilters?: FilterItem[] | Record<string, any>
  onResetFilters?: () => void
  /** Custom left slot (e.g. Fund select for Pipeline) */
  leftSlot?: ReactNode
  /** cfg.help_articles.article_key for this screen. Set it and the ribbon grows a "?" that opens the user guide in a non-modal drawer; omit it and no help button renders. One-prop opt-in — see docs/FRONTEND_DESIGN_PATTERNS.md. */
  helpArticleKey?: string
  /** Renders in the toolbar right cluster (e.g. ViewConfig / Edit columns). Export sits between this and Add when enabled. */
  beforeAddSlot?: ReactNode
  /** Renders immediately before the Add button (after Export) — for secondary add-style actions (e.g. Bulk Add). */
  beforeAddButtonSlot?: ReactNode
  /** Optional state to pass when Add button navigates (e.g. selectedFundId for pipeline) */
  addButtonNavigateState?: Record<string, unknown>
  /** Excel export for the current table view (filters + sort; all pages). */
  exportEnabled?: boolean
  onExport?: () => void | Promise<void>
  exportLoading?: boolean
}

type ViewToolbarProps = ViewToolbarBaseProps & ViewToolbarSearchProps

const ViewToolbar = ({
  // Core
  moduleKey,
  subModuleKey,
  recordTitleField,
  // Search
  searchEnabled = true,
  searchPlaceholder = `Search ${formatKeyToLabel(moduleKey, false)}...`,
  searchClickOpensFullForm = false,
  searchService,
  searchExtraContext,
  variant = 'primary',
  // Add
  addButtonEnabled = true,
  addButtonLabel = moduleKey,
  // Refresh
  refreshButtonEnabled = true,
  onRefreshFilterOptions = () => {},
  onRefreshData = () => {},
  // Filters
  activeFilters = [],
  currentFilterOptions = {},
  onFilterChange = () => {},
  filterOptionsLoading = false,
  defaultFilters,
  onResetFilters,
  leftSlot,
  helpArticleKey,
  beforeAddSlot,
  beforeAddButtonSlot,
  addButtonNavigateState,
  exportEnabled = false,
  onExport,
  exportLoading = false
}: ViewToolbarProps) => {
  const { openFormInTab } = useTabManagerContext()
  const { openDrawerForRecord } = useDrawer()

  const moduleIdField = `${moduleKey}${FIELD_KEY_SEPARATOR}${TABLE_PRIMARY_KEY}`
  // Only reached from the search popover, where the prop type guarantees recordTitleField is set.
  const recordTitle = (record: Record<string, any>) => (recordTitleField ? record[recordTitleField] : undefined)

  const rightContent = (
    <div className='flex items-center gap-3 flex-wrap'>
      <div className='flex min-w-0 items-center gap-2 flex-wrap'>
        {helpArticleKey && <HelpButton articleKey={helpArticleKey} variant='ribbon' />}
        {beforeAddSlot}
        {exportEnabled && onExport && (
          <ExportToolbarButton onClick={onExport} loading={exportLoading} disabled={exportLoading} />
        )}
      </div>

      {/* Secondary add-style actions, grouped with the Add button */}
      {beforeAddButtonSlot}

      {/* Add Button */}
      {addButtonEnabled && (
        <AddButton
          addButtonLabel={formatKeyToLabel(addButtonLabel)}
          moduleKey={moduleKey}
          subModuleKey={subModuleKey}
          navigateState={addButtonNavigateState}
        />
      )}

      {/* Search Popover in Right */}
      {searchEnabled && (
        <SearchPopover
          placeholder={searchPlaceholder}
          onItemClick={(record: Record<string, any>) =>
            searchClickOpensFullForm
              ? openFormInTab(moduleKey, record[moduleIdField], recordTitle(record))
              : openDrawerForRecord(record[moduleIdField])
          }
          onExpandClick={(record: Record<string, any>) => {
            openFormInTab(moduleKey, record[moduleIdField], recordTitle(record))
          }}
          searchService={searchService ?? moduleKey}
          searchExtraContext={searchExtraContext}
          className='max-w-md'
        />
      )}

      {/* Refresh Button */}
      {refreshButtonEnabled && (
        <RefreshButton
          activeFilters={activeFilters}
          onRefreshFilterOptions={onRefreshFilterOptions}
          onRefreshData={onRefreshData}
        />
      )}
    </div>
  )

  const backgroundClass =
    variant === 'secondary'
      ? 'bg-[linear-gradient(135deg,#94A3B8_0%,#64748B_45%,#475569_100%)]'
      : 'bg-[linear-gradient(135deg,#6A96DE_0%,#4C90E9_25%,#65A9DF_50%,#2887D0_75%,#1155CC_100%)]'

  return (
    <div className={`mt-1 flex-shrink-0 mb-2`}>
      <div className={`py-2 px-6 shadow-md rounded-lg ${backgroundClass}`}>
        <div className='flex flex-col lg:flex-row lg:items-center lg:justify-between w-full gap-3'>
          {/* Left Content */}
          <div className='flex items-center gap-3'>{leftSlot}</div>

          {/* Right Content */}
          <div className='flex items-center gap-3 flex-wrap'>{rightContent}</div>
        </div>
      </div>
    </div>
  )
}

export default ViewToolbar
